import {useEffect, useMemo, useRef} from 'react';

import {TInterval} from 'shared/types';
import {useWebsocketInstance} from 'websockets/core/use-websocket-instance';
import {TWebsocketMessageHandlers, IWebsocketResponseMessage} from 'websockets/core/websockets-types';

interface ICreateWebsocketSubscriberInstanceArgs {
    handlers: TWebsocketMessageHandlers;
}

export const createWebsocketSubscriberInstance = ({
    handlers,
}: ICreateWebsocketSubscriberInstanceArgs) => {
    const send = (
        messageType: IWebsocketResponseMessage<any>['type'],
        messageParsed: IWebsocketResponseMessage<any>,
    ) => {
        if (handlers?.[messageType]) handlers[messageType]?.(messageParsed);
    };
    return {
        send,
    };
};

export type TWebsocketSubscriberInstance = ReturnType<typeof createWebsocketSubscriberInstance>;

type TWebsocketSubscriberFallback = {
    execute: () => void;
    interval: number;
}

export const useWebsocketSubscriber = (
    handlers: TWebsocketMessageHandlers,
    fallback?: {
        fallbacks?: TWebsocketSubscriberFallback[];
        deps?: any[];
        onError?: () => void;
    },
) => {
    // Subscription ↴
    const {wsInstance} = useWebsocketInstance();

    const wsSubscriber = useMemo(() => createWebsocketSubscriberInstance({
        handlers,
    }), [handlers]);

    useEffect(() => {
        wsInstance?.subscribe(wsSubscriber);
        return () => {
            wsInstance?.unsubscribe(wsSubscriber);
        };
    }, [handlers]);

    // Fallback ↴
    const {deps, fallbacks, onError} = fallback ?? {};
    const fallbackIntervalsRef = useRef<TInterval[]>([]);

    useEffect(() => {
        if (!wsInstance?.isWithError) return () => {};
        onError?.();

        fallbacks?.forEach(fb => {
            fallbackIntervalsRef.current.push(setInterval(() => {
                fb.execute();
            }, fb.interval));
        });

        return () => {
            fallbackIntervalsRef.current.forEach(fb => {
                clearInterval(fb);
            });
            fallbackIntervalsRef.current = [];
        };
    }, [wsInstance?.isWithError, ...(deps ?? [])]);
};
